/**
 * Task 37 — the only place the ConnectWise sync screens talk to data.
 *
 * Every screen calls this module and nothing else. No component anywhere builds a billing sync URL
 * or header of its own, which is what keeps a change of endpoint or header a change in one file.
 * The same shape as Helpers/SuppressionApi.js, for the same reasons.
 *
 * THE RESELLER IS NOT SENT FROM HERE. It is resolved at the front-end API from the signed
 * CloudCoverToken and forwarded to the backend as a trusted header. The browser holds a ResellerID
 * in session storage, but that value is for deciding what to render, never for deciding what data
 * to return — and Helpers/DeCryptParameter.js is a no-op, so nothing there is protected either.
 */
import Axios from "axios";
import { API_ROUTE, Routes } from "./Constants";

/**
 * Builds the headers every authenticated call in this SPA sends.
 *
 * Every function below routes through here, so there is one place that knows the header pair.
 */
async function buildConfig(setAuthContext, resellerId) {
    // Store/Authentication-Context.js takes a single argument. Several older pages call it as
    // setAuthContext("", new Date()), which silently discards the timestamp and disables the
    // 20-minute refresh check — do not copy that here.
    const token = await setAuthContext(new Date());
    const headers = {
        Authorisation: `Bearer ${token}`,
        CloudCoverToken: sessionStorage.getItem("CloudCoverToken"),
    };

    // Which reseller to act on, when a master administrator has chosen one other than their own.
    // Omitted entirely otherwise, which is what keeps the ordinary path behaving exactly as before.
    //
    // THIS IS NOT AN AUTHORISATION INPUT. The front-end API decides the reseller from the signed
    // token and only honours this after confirming the caller may act on somebody else's behalf —
    // see Services/Helpers/ResellerScopeResolver.cs. Sending it proves nothing.
    if (resellerId) headers.ManageResellerId = String(resellerId);

    return { headers };
}

/**
 * Shared failure handling. A 440 means the CloudCoverToken expired; every page in this app responds
 * by sending the user to the timeout handler, so do it here once.
 *
 * ALWAYS RETURNS A STRING (or null for the redirect case). Callers hand the result straight to
 * toast.error, which renders it as a React child, so an object would surface as "[object Object]"
 * or throw during render.
 *
 * The two refusal shapes on this feature disagree, which is why this cannot just return
 * response.data:
 *   - the access policy filter -> { "message": "Forbidden" }   (an object)
 *   - the proxy and backend    -> "Forbidden: ..."             (a string)
 */
export function handleApiError(err, fallbackMessage) {
    if (err?.response?.status === 440) {
        window.location.href = "/TimeoutErrorHandler";
        return null;
    }

    const data = err?.response?.data;
    if (typeof data === "string" && data.trim()) return data;
    if (data && typeof data.message === "string" && data.message.trim()) return data.message;

    return fallbackMessage;
}

/**
 * The reseller's ConnectWise connection.
 *
 * Never carries the private key. `hasPrivateKey` says whether one is stored, so the form can offer
 * "leave blank to keep the existing key" without the key reaching the browser.
 */
export async function getConfig(setAuthContext, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.get(API_ROUTE + Routes.BILLING_SYNC_CONFIG, config);
    return response.data;
}

/**
 * Proves a set of credentials without saving them.
 *
 * Returns { success, message, agreementsVisible }. A failed test is an ordinary 200 carrying
 * success:false — the reason is for the reseller to act on, not an error to swallow.
 */
export async function testConnection(setAuthContext, connection, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.post(API_ROUTE + Routes.BILLING_SYNC_TEST_CONNECTION, connection, config);
    return response.data;
}

/**
 * Saves the connection, encrypting the private key server-side.
 *
 * The server re-runs the test before writing, so a save can still fail even after the screen has seen
 * a passing test — the two calls are separated in time. A refusal arrives as a 400 whose body is the
 * same result shape, which handleApiError turns into the message.
 *
 * Leave `privateKey` blank to keep the key already stored.
 */
export async function saveConfig(setAuthContext, connection, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.post(API_ROUTE + Routes.BILLING_SYNC_CONFIG, connection, config);
    return response.data;
}

/**
 * Whether this user may work on a reseller other than their own.
 *
 * Asked BEFORE the picker is offered, and answered by the same resolver that guards every other
 * call on these screens. The picker used to be shown on the strength of the browser's own
 * ResellerID and a reseller list fetched under a weaker gate — neither of which is the test the
 * server applies — so a master administrator whose identity could not be confirmed was given a
 * control that failed the moment it was used.
 */
export async function getScope(setAuthContext) {
    const config = await buildConfig(setAuthContext);
    const response = await Axios.get(API_ROUTE + Routes.BILLING_SYNC_SCOPE, config);
    return response.data;
}

/**
 * The resellers a master administrator may work on.
 *
 * Reuses the existing master-admin endpoint rather than adding one — it is already gated on the
 * master-admin policy, so a reseller administrator calling it is refused there rather than here.
 */
export async function getResellers(setAuthContext) {
    const config = await buildConfig(setAuthContext);
    // THE BODY MUST BE AN EMPTY STRING, NOT {}. RBACAuthenticationAttribute skips its ResellerID
    // check only when the body is null or "" (Services/Helpers/AuthorizeAttribute.cs); anything
    // else is deserialised into a ResellerOrgs and compared against the token claim, and {} yields
    // ResellerID = null, which never matches — so every call came back 403 "ResellerID mismatch"
    // and the master administrator's picker never appeared.
    //
    // The three other callers of this endpoint (MasterAdmin.js twice, PriceManagement.js) all send
    // "" for the same reason. This looks like a typo and is not.
    const response = await Axios.post(API_ROUTE + Routes.GET_MASTERADMIN_RESELLERS, "", config);
    return response.data;
}

// ---- Phase 2: configuration ----

/**
 * The reseller's ConnectWise companies. One of the few live reads — a mapping cannot be made
 * without seeing what is there to map to.
 */
export async function getCompanies(setAuthContext, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.get(API_ROUTE + Routes.BILLING_SYNC_COMPANIES, config);
    return response.data;
}

/** The agreements belonging to one company. Also live, for the same reason. */
export async function getAgreements(setAuthContext, companyId, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.get(API_ROUTE + Routes.BILLING_SYNC_AGREEMENTS(companyId), config);
    return response.data;
}

/**
 * The reseller's customers with their current mapping.
 *
 * Read entirely from our own records — the company and agreement names were stored when the mapping
 * was saved, so showing them back does not depend on ConnectWise being reachable.
 */
export async function getCustomers(setAuthContext, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.get(API_ROUTE + Routes.BILLING_SYNC_CUSTOMERS, config);
    return response.data;
}

/**
 * Attaches a customer to a company and agreement and sets who owns pricing.
 *
 * Can be refused for a reason the reseller can act on — an agreement that cannot move while billing
 * lines are still open — which arrives as a 400 carrying the message.
 */
export async function saveCustomerMapping(setAuthContext, mapping, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.post(API_ROUTE + Routes.BILLING_SYNC_CUSTOMER_MAPPING, mapping, config);
    return response.data;
}

/** The reseller's own product catalogue, for the mapping picker. */
export async function getCwProducts(setAuthContext, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.get(API_ROUTE + Routes.BILLING_SYNC_CW_PRODUCTS, config);
    return response.data;
}

/**
 * Which packages this reseller bills, what each maps to, and whether the set is complete.
 *
 * Completeness is decided by the server, because the same answer gates enabling sync — working it
 * out again in the browser would be a second opinion that could disagree.
 */
export async function getProductMappings(setAuthContext, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.get(API_ROUTE + Routes.BILLING_SYNC_PRODUCT_MAPPINGS, config);
    return response.data;
}

/** Saves one package's licence and storage product mappings. A null id clears that side. */
export async function saveProductMapping(setAuthContext, mapping, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.post(API_ROUTE + Routes.BILLING_SYNC_PRODUCT_MAPPINGS, mapping, config);
    return response.data;
}

// ---- Phase 3: dry run, activation, discard ----

/**
 * Asks for a dry run to be prepared. Returns as soon as the work is queued, not when it is done —
 * the screen then polls getDryRun until it appears.
 */
export async function prepareDryRun(setAuthContext, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.post(API_ROUTE + Routes.BILLING_SYNC_PREPARE_DRY_RUN, null, config);
    return response.data;
}

/** The prepared dry run, or a summary saying none has been prepared. */
export async function getDryRun(setAuthContext, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.get(API_ROUTE + Routes.BILLING_SYNC_DRY_RUN, config);
    return response.data;
}

/** Whether sync is on, whether it could be, and what stands in the way. */
export async function getSyncState(setAuthContext, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.get(API_ROUTE + Routes.BILLING_SYNC_STATE, config);
    return response.data;
}

/**
 * Work calculated but not yet sent, charges and cancellations kept apart. Discarded lines are always
 * counted, and returned only when asked for (N3).
 */
export async function getPendingWork(setAuthContext, resellerId, includeDiscarded) {
    const config = await buildConfig(setAuthContext, resellerId);
    const route = `${Routes.BILLING_SYNC_PENDING_WORK}?includeDiscarded=${includeDiscarded ? "true" : "false"}`;
    const response = await Axios.get(API_ROUTE + route, config);
    return response.data;
}

export async function enableSync(setAuthContext, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.post(API_ROUTE + Routes.BILLING_SYNC_ENABLE, null, config);
    return response.data;
}

export async function disableSync(setAuthContext, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.post(API_ROUTE + Routes.BILLING_SYNC_DISABLE, null, config);
    return response.data;
}

/** Throws away the selected lines. Identifiers, never "everything". */
export async function discardWork(setAuthContext, billingSyncItemIds, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.post(
        API_ROUTE + Routes.BILLING_SYNC_DISCARD, { billingSyncItemIds }, config);
    return response.data;
}

/** Restores the selected discarded lines (N4). Identifiers, never "everything" — as Discard. */
export async function rebuildWork(setAuthContext, billingSyncItemIds, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.post(
        API_ROUTE + Routes.BILLING_SYNC_REBUILD, { billingSyncItemIds }, config);
    return response.data;
}

/**
 * Approves the current preview for sending — "Queue for syncing" (N1).
 *
 * Sends only WHICH preview was on screen; the server decides what it contains. `preparedAt` must be
 * the value exactly as the server sent it. Turning it into a Date would drop the microseconds the
 * server compares against, and every approval would then look like it was for an older preview.
 */
export async function queueForSync(setAuthContext, preparedAt, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.post(
        API_ROUTE + Routes.BILLING_SYNC_QUEUE_FOR_SYNC, { preparedAt }, config);
    return response.data;
}

/**
 * Records that this person is on the screen and reports whoever else is.
 *
 * The acting email is NOT sent from here — the front-end API resolves it from the signed token,
 * for the same reason the reseller is resolved there.
 */
export async function touchPresence(setAuthContext, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.post(API_ROUTE + Routes.BILLING_SYNC_PRESENCE, null, config);
    return response.data;
}

export async function releasePresence(setAuthContext, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.post(API_ROUTE + Routes.BILLING_SYNC_PRESENCE_RELEASE, null, config);
    return response.data;
}

// ---- Phase 4: status and retry ----

/**
 * Where each of the reseller's billing lines has got to, with the reason for anything that failed.
 *
 * Bounded by the server to the current and previous billing period, and excluding acknowledged
 * lines unless asked for them.
 */
export async function getSyncStatus(setAuthContext, resellerId, includeAcknowledged) {
    const config = await buildConfig(setAuthContext, resellerId);
    const route = `${Routes.BILLING_SYNC_STATUS}?includeAcknowledged=${includeAcknowledged ? "true" : "false"}`;
    const response = await Axios.get(API_ROUTE + route, config);
    return response.data;
}

/**
 * Takes failed lines out of the active view.
 *
 * NOT a discard, and the wording everywhere this surfaces has to keep saying so: a line reaches
 * this point only after failing, and a failed line may already have reached ConnectWise in part.
 */
export async function acknowledgeLines(setAuthContext, billingSyncItemIds, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.post(
        API_ROUTE + Routes.BILLING_SYNC_ACKNOWLEDGE, { billingSyncItemIds }, config);
    return response.data;
}

/** Whether a background retry run is under way, and how the last one ended. */
export async function getRetryState(setAuthContext, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.get(API_ROUTE + Routes.BILLING_SYNC_RETRY_STATE, config);
    return response.data;
}

/**
 * Returns everything that failed to the queue.
 *
 * Answers as soon as the run is queued, not when it is done — the client asked for this to be a
 * background job, so the screen then polls getRetryState.
 */
export async function retryAll(setAuthContext, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.post(API_ROUTE + Routes.BILLING_SYNC_RETRY, null, config);
    return response.data;
}

/** Returns one line to the queue. One row, so this one answers immediately. */
export async function retryLine(setAuthContext, billingSyncItemId, resellerId) {
    const config = await buildConfig(setAuthContext, resellerId);
    const response = await Axios.post(
        API_ROUTE + Routes.BILLING_SYNC_RETRY_LINE(billingSyncItemId), null, config);
    return response.data;
}
